import React, { useRef, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import classnames from 'classnames';
import { Track } from 'twilio-video';
import { IVideoTrack } from 'types';
import { useMediaStreamTrack, useVideoTrackDimensions } from 'hooks';

const Video = styled('video')({
  width: '100%',
  height: '100%',
});

type VideoTrackProps = {
  track: IVideoTrack;
  isLocal?: boolean;
  priority?: Track.Priority | null;
  zoomScale?: number;
  panOffset?: {
    x: number;
    y: number;
  };
  canPan?: boolean;
};

export function VideoTrack({ track, isLocal, priority, zoomScale = 1, panOffset, canPan = false }: VideoTrackProps) {
  const ref = useRef<HTMLVideoElement>(null!);
  const mediaStreamTrack = useMediaStreamTrack(track);
  const dimensions = useVideoTrackDimensions(track);
  const isPortrait = (dimensions?.height ?? 0) > (dimensions?.width ?? 0);

  useEffect(() => {
    const el = ref.current;
    el.muted = true;
    if (track.setPriority && priority) {
      track.setPriority(priority);
    }
    track.attach(el);
    return () => {
      track.detach(el);

      // This addresses a Chrome issue where the number of WebMediaPlayers is limited.
      // See: https://github.com/twilio/twilio-video.js/issues/1528
      el.srcObject = null;

      if (track.setPriority && priority) {
        // Passing `null` to setPriority will set the track's priority to that which it was published with.
        track.setPriority(null);
      }
    };
  }, [track, priority]);

  // The local video track is mirrored if it is not facing the environment.
  const isFrontFacing = mediaStreamTrack?.getSettings().facingMode !== 'environment';
  const style = {
    transform: `${
      zoomScale !== 1 ? `translate(${panOffset?.x ?? 0}px, ${panOffset?.y ?? 0}px) scale(${zoomScale}) ` : ''
    }${isLocal && isFrontFacing ? 'scaleX(-1)' : ''}`.trim(),
    transformOrigin: 'center center',
    objectFit: isPortrait || track.name.includes('screen') ? ('contain' as const) : ('cover' as const),
  };

  return <Video ref={ref} style={style} className={classnames({ 'emdr-pan-video': canPan && zoomScale > 1 })} />;
}
